import React, { useCallback, memo, useRef, useEffect } from 'react';
import { Box, Typography, Dialog, Button } from '@mui/material';
import CloseIconButton from '../../../../components/common/CloseIconButton';

/**
 * =============================================================================
 * InsufficientAdvancePopup Component
 * =============================================================================
 * 
 * This popup is displayed when the package advance validation fails
 * (i.e., validatePackageAdvance API returns overall_valid === false).
 * 
 * WHEN IS THIS SHOWN?
 * - User clicks "Collect Payment" on an invoice with package-linked services
 * - API validates if sufficient package advance exists
 * - If NOT sufficient → This popup appears
 * 
 * USER FLOW:
 * 1. Popup shows warning message about insufficient advance
 * 2. User clicks "Collect Package Advance" button
 * 3. Popup closes and CollectPackageAdvanceModal opens (3-step flow)
 * 4. After advance is collected, user can retry "Collect Payment"
 * 
 * PROPS:
 * @param open - Controls popup visibility
 * @param onClose - Called when popup is dismissed (close button or backdrop click)
 * @param onCollectAdvance - Called when user clicks "Collect Package Advance"
 *                           Opens the 3-step package advance collection flow
 */
interface InsufficientAdvancePopupProps {
  /** Controls whether the popup is visible */
  open: boolean;
  /** Handler called when popup is closed without action */
  onClose: () => void;
  /** Handler called when user confirms to collect advance - opens 3-step flow */
  onCollectAdvance: () => void;
  /** Optional: Custom title text */
  title?: string;
  /** Optional: Custom message text */
  message?: string;
}

/**
 * Default messages for the popup
 */
const DEFAULT_TITLE = 'Insufficient Package Advance';
const DEFAULT_MESSAGE = 
  'The current package advance amount does not fully cover the selected services. ' +
  'Please collect the required package advance to continue with payment.';

/**
 * InsufficientAdvancePopup - Memoized component for performance
 */
function InsufficientAdvancePopupComponent(props: InsufficientAdvancePopupProps) {
  const {
    open,
    onClose,
    onCollectAdvance,
    title = DEFAULT_TITLE,
    message = DEFAULT_MESSAGE,
  } = props;

  // Ref to track setTimeout for cleanup on unmount
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timer on unmount to prevent stale state updates
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  /**
   * Memoized confirm handler to prevent unnecessary re-renders
   * Ensures popup closes before triggering the advance collection flow
   */
  const handleConfirm = useCallback(() => {
    // Validate that onCollectAdvance is a valid function before calling
    if (typeof onCollectAdvance !== 'function') {
      // eslint-disable-next-line no-console
      console.error('InsufficientAdvancePopup: onCollectAdvance prop must be a function');
      onClose();
      return;
    }
    
    // Close this popup first
    onClose();
    
    // Small delay to ensure smooth transition between modals
    // This prevents jarring visual effects when one modal closes and another opens
    // Store timer ref for cleanup on unmount
    timerRef.current = setTimeout(() => {
      onCollectAdvance();
    }, 150);
  }, [onClose, onCollectAdvance]);

  /**
   * Memoized close handler
   */
  const handleClose = useCallback(() => {
    if (typeof onClose === 'function') {
      onClose();
    }
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          minWidth: 400,
          maxWidth: 480,
          p: 0,
          overflow: 'visible',
        },
      }}
      aria-labelledby="insufficient-advance-title"
      aria-describedby="insufficient-advance-description"
    >
      {/* Close Icon Button - Top Right */}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 1,
        }}
      >
        <CloseIconButton 
          onClick={handleClose}
          aria-label="Close popup"
        />
      </Box>

      {/* Content Container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 5,
          pb: 3,
          px: 3,
        }}
      >
        {/* Warning Icon - Orange circle with exclamation mark */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: '#FFF3ED',
            mb: 2.5,
            flexShrink: 0,
          }}
          aria-hidden="true"
        >
          <Box
            component="span"
            sx={{
              color: '#E17E61',
              fontSize: '2rem',
              fontWeight: 'bold',
              lineHeight: 1,
            }}
          >
            !
          </Box>
        </Box>

        {/* Title */}
        <Typography
          variant="h6"
          component="h2"
          sx={{ 
            fontWeight: 600, 
            textAlign: 'center', 
            mb: 1.5,
            fontSize: '1.25rem',
          }}
          id="insufficient-advance-title"
        >
          {title}
        </Typography>

        {/* Descriptive Message */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ 
            textAlign: 'center', 
            mb: 3, 
            px: 1, 
            lineHeight: 1.6,
            maxWidth: 380,
            whiteSpace: 'pre-line',
          }}
          id="insufficient-advance-description"
        >
          {message}
        </Typography>

        {/* Collect Package Advance Button */}
        <Button
          variant="contained"
          onClick={handleConfirm}
          fullWidth
          sx={{
            borderRadius: '8px',
            py: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            backgroundColor: '#505050',
            '&:hover': {
              backgroundColor: '#3a3a3a',
            },
          }}
          aria-describedby="insufficient-advance-description"
        >
          Collect Package Advance
        </Button>
      </Box>
    </Dialog>
  );
}

// Memoized export for performance
const InsufficientAdvancePopup = memo(InsufficientAdvancePopupComponent);

// Display name for debugging
InsufficientAdvancePopup.displayName = 'InsufficientAdvancePopup';

export default InsufficientAdvancePopup;
